// src/pages/MesCoursProf.js - VERSION RICH UI
import React, { useEffect, useState } from "react";
import FicheEleveProgression from '../components/learning/FicheEleveProgression';
import axios from "axios";
import { supabase } from "../supabaseClient";
import { GraduationCap, ClipboardList, Calendar, Clock, Video } from 'lucide-react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Marcellus&family=DM+Sans:wght@300;400;500;600&display=swap');

  .mescours-wrap { font-family: 'DM Sans', sans-serif; color: #1a1a1a; }

  .mescours-empty { text-align: center; padding: 40px 20px; color: #bbb; }
  .mescours-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.35; }
  .mescours-empty p { font-size: 13px; }

  .mescours-loading { text-align: center; padding: 32px; color: #999; font-size: 14px; }
  .spinner-ring-sm {
    width: 32px; height: 32px;
    border: 3px solid #d1fae5;
    border-top-color: #10b981;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 10px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .cours-list { display: flex; flex-direction: column; gap: 12px; }

  .cours-item {
    background: #fff;
    border: 1.5px solid #e8e2d9;
    border-left: 4px solid #10b981;
    border-radius: 12px;
    padding: 16px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
    transition: box-shadow 0.2s;
  }
  .cours-item:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }

  .cours-item-left { display: flex; align-items: center; gap: 12px; flex: 1; }
  .cours-eleve-av {
    width: 40px; height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #059669);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-weight: 700; font-size: 14px; flex-shrink: 0;
  }
  .cours-eleve-name { font-weight: 700; font-size: 14px; color: #1a1a1a; margin-bottom: 3px; }
  .cours-datetime { font-size: 12px; color: #888; display: flex; gap: 10px; flex-wrap: wrap; }

  .cours-item-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .countdown-badge { padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700; }
  .countdown-badge.today  { background: #d1fae5; color: #065f46; }
  .countdown-badge.soon   { background: #fef3c7; color: #92400e; }
  .countdown-badge.future { background: #eff6ff; color: #1d4ed8; }

  .btn-join {
    padding: 8px 16px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff; border: none; border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; text-decoration: none;
    display: inline-flex; align-items: center; gap: 5px;
    transition: all 0.2s; box-shadow: 0 2px 8px rgba(16,185,129,0.3); white-space: nowrap;
  }
  .btn-join:hover { transform: translateY(-1px); }
  .btn-join-disabled {
    padding: 8px 14px; background: #f3f4f6; color: #9ca3af;
    border: none; border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 11px; font-weight: 600;
    cursor: default; white-space: nowrap;
  }
`;

const getInitials = (nom = '') => {
  const p = nom.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : nom.slice(0,2).toUpperCase() || 'E';
};

const getCountdown = (date) => {
  const d = new Date(date), today = new Date();
  d.setHours(0,0,0,0); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return { label: "Aujourd'hui", cls: 'today' };
  if (diff === 1) return { label: 'Demain', cls: 'soon' };
  if (diff > 1)  return { label: `Dans ${diff}j`, cls: 'future' };
  return { label: 'Passé', cls: 'future' };
};

// Priorité à start_utc (fourni par /booking/reservations/me via enrichWithUTC) — fonctionne
// quel que soit le fuseau du navigateur du prof. Fallback : ancien comportement (heure brute
// interprétée dans la TZ du navigateur), utilisé seulement si start_utc est absent.
const isJitsiAvailable = (coursOrDate, heureOptional) => {
  let dt;
  if (coursOrDate && typeof coursOrDate === 'object' && coursOrDate.start_utc) {
    dt = new Date(coursOrDate.start_utc);
  } else if (coursOrDate && typeof coursOrDate === 'object') {
    dt = new Date(`${coursOrDate.date}T${coursOrDate.heure_debut}`);
  } else {
    dt = new Date(`${coursOrDate}T${heureOptional}`);
  }
  const diff = (dt - new Date()) / 60000;
  return diff <= 15 && diff > -60;
};

export default function MesCoursProf() {
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [ficheData, setFicheData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        setSessionToken(token);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/booking/reservations/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const now = new Date();
        // Helpers timezone : priorité aux timestamps UTC fournis par le backend
        const getStart = (r) => r.start_utc ? new Date(r.start_utc) : new Date(`${r.date}T${r.heure_debut}`);
        const getEnd = (r) => r.end_utc ? new Date(r.end_utc) : new Date(`${r.date}T${r.heure_fin}`);
        const avenir = (res.data.reservations || [])
          .filter(r => r.statut === 'confirmé' && getEnd(r) > now)
          .sort((a, b) => getStart(a) - getStart(b));
        setCours(avenir);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="mescours-wrap">
      <style>{styles}</style>
      <div className="mescours-loading"><div className="spinner-ring-sm" />Chargement…</div>
    </div>
  );

  return (
    <>
      <div className="mescours-wrap">
        <style>{styles}</style>
        {cours.length === 0 ? (
          <div className="mescours-empty">
            <div className="mescours-empty-icon">📅</div>
            <p>Aucun cours programmé pour le moment.</p>
          </div>
        ) : (
          <div className="cours-list">
            {cours.map(c => {
              const videoLink = c.jitsi_url || `https://www.firstarabic.com/cours/firstarabic-${c.id}?reservationId=${c.id}`;
              const available = isJitsiAvailable(c);
              const countdown = getCountdown(c.date);
              const nomEleve = c.eleves?.prenom || c.eleves?.nom || 'Eleve';
              return (
                <div key={c.id} className="cours-item">
                  <div className="cours-item-left">
                    <div className="cours-eleve-av">{getInitials(nomEleve)}</div>
                    <div>
                      <div className="cours-eleve-name" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <GraduationCap size={16} strokeWidth={2} color="#065f46" />{nomEleve}
                        </span>
                        <button
                          onClick={() => {
                            setFicheData({
                              eleveId: c.eleves?.created_by,
                              eleveName: nomEleve,
                            });
                            setFicheOpen(true);
                          }}
                          title="Voir la fiche de progression de l'élève"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '5px 12px', borderRadius: 8, border: '1px solid #cdeee1',
                            background: '#E1F5EE', color: '#065f46',
                            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          <ClipboardList size={15} strokeWidth={2} />Fiche de progression
                        </button>
                      </div>
                      <div className="cours-datetime">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Calendar size={14} strokeWidth={2} />{new Date(c.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Clock size={14} strokeWidth={2} />{c.heure_debut?.substring(0,5)} – {c.heure_fin?.substring(0,5)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="cours-item-right">
                    <span className={`countdown-badge ${countdown.cls}`}>{countdown.label}</span>
                    {available
                      ? <a href={videoLink} target="_blank" rel="noopener noreferrer" className="btn-join" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Video size={15} strokeWidth={2} />Rejoindre</a>
                      : <span className="btn-join-disabled">Lien 15 min avant</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {ficheOpen && ficheData && (
        <FicheEleveProgression
          eleveId={ficheData.eleveId}
          eleveName={ficheData.eleveName}
          token={sessionToken}
          onClose={() => { setFicheOpen(false); setFicheData(null); }}
        />
      )}
    </>
  );
}
